import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Pencil, Trash2, History, Search, X, Shield, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import { useAuth } from "../context/AuthContext";
import {
  PERMISSION_MODULES,
  PERMISSION_ACTIONS,
  ROLE_TEMPLATES,
  emptyPermissions,
} from "../lib/permissions";

const ROLE_LABEL = { admin: "Admin", production: "Produktion", warehouse: "Lager", sales: "Verkauf" };
const EMPTY_FORM = { email: "", password: "", name: "", role: "sales" };
const ACTION_LABEL = {
  "user.create": { label: "Angelegt", cls: "bg-emerald-50 text-emerald-700" },
  "user.update": { label: "Geändert", cls: "bg-amber-50 text-amber-800" },
  "user.delete": { label: "Gelöscht", cls: "bg-red-50 text-red-700" },
  "user.permissions": { label: "Rechte", cls: "bg-sky-50 text-sky-700" },
};
const TEST_USER_REGEX = /^(test|test\.|ui_test|sales_|prod_|wh_|sales2?_|prod2?_|admin_test_)/i;
const isTestEmail = (email = "") => TEST_USER_REGEX.test(email.split("@")[0] || "");

// Compact matrix component reused in create + edit dialogs.
function PermissionMatrix({ value, onChange, disabled = false }) {
  const toggle = (mod, action) => {
    if (disabled) return;
    const next = { ...(value || emptyPermissions()) };
    const row = { ...(next[mod] || { read: false, edit: false, delete: false }) };
    row[action] = !row[action];
    // Auto-cascade: enabling edit/delete implies read; disabling read wipes the row.
    if ((action === "edit" || action === "delete") && row[action]) row.read = true;
    if (action === "read" && !row.read) { row.edit = false; row.delete = false; }
    next[mod] = row;
    onChange(next);
  };

  const applyTemplate = (key) => {
    const t = ROLE_TEMPLATES.find((x) => x.key === key);
    if (!t) return;
    onChange(JSON.parse(JSON.stringify(t.permissions)));
    toast.success(`Vorlage „${t.label}" angewendet`);
  };

  return (
    <div className="space-y-3" data-testid="permission-matrix">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
          Rollen-Vorlage:
        </span>
        {ROLE_TEMPLATES.map((t) => (
          <Button
            key={t.key}
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full text-xs"
            disabled={disabled}
            onClick={() => applyTemplate(t.key)}
            data-testid={`btn-template-${t.key}`}
            title={t.description}
          >
            {t.label}
          </Button>
        ))}
      </div>
      <div className="border border-[var(--border)] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-alt)]">
            <tr>
              <th className="text-left px-3 py-2 overline">Modul</th>
              {PERMISSION_ACTIONS.map((a) => (
                <th key={a.key} className="px-3 py-2 overline text-center w-28">{a.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_MODULES.map((m) => {
              const row = (value || {})[m.key] || { read: false, edit: false, delete: false };
              return (
                <tr key={m.key} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2 font-medium">{m.label}</td>
                  {PERMISSION_ACTIONS.map((a) => (
                    <td key={a.key} className="px-3 py-2 text-center">
                      <Checkbox
                        checked={!!row[a.key]}
                        onCheckedChange={() => toggle(m.key, a.key)}
                        disabled={disabled}
                        data-testid={`perm-${m.key}-${a.key}`}
                        aria-label={`${m.label} ${a.label}`}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[var(--text-muted)]">
        „Bearbeiten" und „Löschen" setzen automatisch das Leserecht. Ohne Leserecht ist das Modul für
        den Benutzer komplett unsichtbar (Route zeigt 403).
      </p>
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [newPerms, setNewPerms] = useState(emptyPermissions());
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: "", email: "", name: "", role: "sales", password: "" });
  const [editPerms, setEditPerms] = useState(emptyPermissions());
  const [editIsSuperadmin, setEditIsSuperadmin] = useState(false);
  const [audit, setAudit] = useState([]);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [onlyTest, setOnlyTest] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const confirm = useConfirm();
  const isSuperadmin = !!me?.is_superadmin;

  const load = async () => setUsers((await api.get("/auth/users")).data);
  const loadAudit = async () => {
    try { setAudit((await api.get("/audit/users?limit=100")).data); } catch { /* noop */ }
  };
  useEffect(() => { load(); loadAudit(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, permissions: newPerms };
      const { data: created } = await api.post("/auth/register", payload);
      // If matrix differs from what /register accepted (older backends) push it via the perms endpoint.
      if (created?.id && isSuperadmin) {
        try { await api.patch(`/auth/users/${created.id}/permissions`, { permissions: newPerms }); } catch { /* noop */ }
      }
      toast.success("Benutzer angelegt");
      setOpen(false); setForm(EMPTY_FORM); setNewPerms(emptyPermissions()); load(); loadAudit();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const openEdit = (u) => {
    setEditForm({ id: u.id, email: u.email, name: u.name || "", role: u.role || "sales", password: "" });
    setEditPerms(u.permissions || emptyPermissions());
    setEditIsSuperadmin(!!u.is_superadmin);
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: editForm.name, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/auth/users/${editForm.id}`, payload);
      // Permissions endpoint is separate and superadmin-only.  Skip for superadmin targets.
      if (isSuperadmin && !editIsSuperadmin) {
        await api.patch(`/auth/users/${editForm.id}/permissions`, { permissions: editPerms });
      }
      toast.success("Benutzer aktualisiert");
      setEditOpen(false); load(); loadAudit();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const remove = async (u) => {
    if (u.is_superadmin) { toast.error("Superadmin kann nicht gelöscht werden"); return; }
    const ok = await confirm({
      title: `Benutzer löschen?`,
      description: `"${u.name || u.email}" wird dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.`,
      confirmLabel: "Löschen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("Benutzer gelöscht");
      load(); loadAudit();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const q = query.trim().toLowerCase();
  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (onlyTest && !isTestEmail(u.email)) return false;
    if (!q) return true;
    return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
  });

  const selectableIds = filtered.filter((u) => !(me && me.id === u.id) && !u.is_superadmin).map((u) => u.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allSelected) { setSelected(new Set()); return; }
    setSelected(new Set(selectableIds));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `${ids.length} Benutzer löschen?`,
      description: "Diese Aktion kann nicht rückgängig gemacht werden. Superadmin, letzter Admin und dein eigener Account werden automatisch übersprungen.",
      confirmLabel: `${ids.length} löschen`,
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/auth/users/bulk-delete", { ids });
      const skipped = data.skipped?.length || 0;
      toast.success(`${data.deleted} Benutzer gelöscht${skipped ? ` · ${skipped} übersprungen` : ""}`);
      if (skipped) {
        data.skipped.forEach((s) => toast.warning(`${s.id.slice(-8)}: ${s.reason}`));
      }
      clearSelection();
      load(); loadAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk-Löschen fehlgeschlagen");
    }
  };

  const permSummary = (u) => {
    if (u.is_superadmin) return "Alle Rechte";
    const perms = u.permissions || {};
    let r = 0, w = 0, d = 0;
    for (const m of PERMISSION_MODULES) {
      const p = perms[m.key] || {};
      if (p.read) r++;
      if (p.edit) w++;
      if (p.delete) d++;
    }
    if (r === 0 && w === 0 && d === 0) return "Keine Rechte";
    return `${r}× R · ${w}× E · ${d}× D`;
  };

  return (
    <div data-testid="page-users">
      <PageHeader
        eyebrow="Team"
        title="Benutzer & Rechte"
        description="Superadmin verwaltet Zugriff auf alle Module (Lesen · Bearbeiten · Löschen)."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-user" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"><Plus size={16} className="mr-1" /> Neuer Benutzer</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">Neuer Benutzer</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Name *</Label><Input required data-testid="input-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Email *</Label><Input required type="email" data-testid="input-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div><Label>Passwort *</Label><Input required type="password" minLength={6} data-testid="input-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                  <div>
                    <Label>Rolle (Anzeigelabel)</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="select-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="production">Produktion</SelectItem>
                        <SelectItem value="warehouse">Lager</SelectItem>
                        <SelectItem value="sales">Verkauf</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Berechtigungen</Label>
                  <PermissionMatrix value={newPerms} onChange={setNewPerms} disabled={!isSuperadmin} />
                  {!isSuperadmin && (
                    <p className="text-xs text-amber-700 mt-2">
                      Nur der Superadmin darf Rechte vergeben. Benutzer wird ohne Rechte angelegt.
                    </p>
                  )}
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-save-user" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Speichern</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="users-filter-bar">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            placeholder="Suche nach Name oder Email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="input-users-search"
            className="rounded-full pl-9"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-black"
              data-testid="btn-clear-search"
              type="button"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger data-testid="select-role-filter" className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Rollen</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="production">Produktion</SelectItem>
            <SelectItem value="warehouse">Lager</SelectItem>
            <SelectItem value="sales">Verkauf</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm cursor-pointer bg-white border border-[var(--border)] rounded-full px-3 py-2">
          <Checkbox
            checked={onlyTest}
            onCheckedChange={(v) => setOnlyTest(!!v)}
            data-testid="checkbox-only-test-users"
          />
          <span>Nur Testbenutzer</span>
        </label>
        <div className="ml-auto text-xs text-[var(--text-muted)]" data-testid="users-count">
          {filtered.length} von {users.length}
        </div>
      </div>

      {selected.size > 0 && (
        <div
          className="sticky top-2 z-30 mb-4 rounded-2xl bg-black text-white p-4 flex items-center justify-between gap-4 shadow-2xl shadow-black/40"
          data-testid="bulk-toolbar"
        >
          <div className="text-sm">
            <span className="font-bold">{selected.size}</span> ausgewählt
          </div>
          <div className="flex gap-2">
            <Button
              onClick={clearSelection}
              variant="outline"
              size="sm"
              className="rounded-full bg-transparent text-white border-white/40 hover:bg-white/10"
              data-testid="btn-clear-selection"
            >
              Abwählen
            </Button>
            <Button
              onClick={bulkDelete}
              size="sm"
              className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold"
              data-testid="btn-bulk-delete"
            >
              <Trash2 size={14} className="mr-1" /> {selected.size} löschen
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={toggleAll}
                  data-testid="checkbox-select-all"
                  aria-label="Alle auswählen"
                />
              </TableHead>
              <TableHead className="overline">Name</TableHead>
              <TableHead className="overline">Email</TableHead>
              <TableHead className="overline">Rolle</TableHead>
              <TableHead className="overline">Rechte</TableHead>
              <TableHead className="overline">Angelegt</TableHead>
              <TableHead className="overline text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => {
              const isSelf = me && me.id === u.id;
              const isLocked = u.is_superadmin;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(u.id)}
                      onCheckedChange={() => toggleOne(u.id)}
                      disabled={isSelf || isLocked}
                      data-testid={`checkbox-user-${u.email}`}
                      aria-label={`${u.email} auswählen`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {u.name}
                      {u.is_superadmin && (
                        <span
                          className="pop-pill bg-[var(--pop-yellow)]/25 text-[var(--pop-caramel)] font-bold"
                          data-testid={`superadmin-badge-${u.email}`}
                          title="Superadmin – geschützter Account"
                        >
                          <ShieldCheck size={12} className="inline mr-1" /> Superadmin
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{u.email}</TableCell>
                  <TableCell><span className="pop-pill bg-[var(--surface-alt)] text-[var(--pop-caramel)]">{ROLE_LABEL[u.role] || u.role}</span></TableCell>
                  <TableCell className="text-xs text-[var(--text-muted)]" data-testid={`perm-summary-${u.email}`}>
                    {permSummary(u)}
                  </TableCell>
                  <TableCell className="text-xs font-mono">{u.created_at?.slice(0, 10) || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        data-testid={`btn-edit-user-${u.email}`}
                        onClick={() => openEdit(u)}
                      >
                        <Pencil size={14} className="mr-1" /> Bearbeiten
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full text-red-600 hover:bg-red-50 disabled:opacity-40"
                        data-testid={`btn-delete-user-${u.email}`}
                        disabled={isSelf || isLocked}
                        title={isLocked ? "Superadmin ist geschützt" : (isSelf ? "Man kann sich nicht selbst löschen" : "Benutzer löschen")}
                        onClick={() => remove(u)}
                      >
                        <Trash2 size={14} className="mr-1" /> Löschen
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">Keine Benutzer gefunden</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <section className="mt-8" data-testid="user-audit-log">
        <div className="flex items-center gap-2 mb-3">
          <History size={16} className="text-[var(--pop-caramel)]" />
          <h2 className="font-display font-bold text-xl">Aktivitätsprotokoll</h2>
          <span className="text-xs text-[var(--text-muted)]">letzte {audit.length} Einträge</span>
        </div>
        <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
          <Table className="table-zebra">
            <TableHeader>
              <TableRow>
                <TableHead className="overline">Zeit</TableHead>
                <TableHead className="overline">Aktion</TableHead>
                <TableHead className="overline">Ausgeführt von</TableHead>
                <TableHead className="overline">Betrifft</TableHead>
                <TableHead className="overline">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {audit.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">Noch keine Aktivität aufgezeichnet</TableCell></TableRow>
              )}
              {audit.map((a) => {
                const badge = ACTION_LABEL[a.action] || { label: a.action, cls: "bg-neutral-100 text-neutral-700" };
                let detail = "";
                if (a.action === "user.update" && a.details?.changes) {
                  detail = Object.entries(a.details.changes).map(([k, v]) => `${k}=${v}`).join(", ");
                } else if (a.action === "user.create") {
                  detail = `${a.details?.role || "sales"}`;
                } else if (a.action === "user.delete") {
                  detail = `${a.details?.role || ""}`.trim();
                } else if (a.action === "user.permissions") {
                  const p = a.details?.permissions || {};
                  const summary = PERMISSION_MODULES
                    .map((m) => {
                      const row = p[m.key] || {};
                      const flags = ["r", "e", "d"].filter((_, i) => row[["read","edit","delete"][i]]).join("");
                      return flags ? `${m.key}:${flags}` : null;
                    })
                    .filter(Boolean)
                    .join(" · ");
                  detail = summary || "alles entzogen";
                }
                return (
                  <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {a.at ? new Date(a.at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "medium" }) : "—"}
                    </TableCell>
                    <TableCell>
                      <span className={`pop-pill ${badge.cls}`}>{badge.label}</span>
                    </TableCell>
                    <TableCell className="text-sm">{a.actor_name || a.actor_email || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{a.details?.email || a.target_id?.slice(-8)}</TableCell>
                    <TableCell className="text-xs text-[var(--text-muted)]">{detail}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              Benutzer bearbeiten
              {editIsSuperadmin && (
                <span className="pop-pill bg-[var(--pop-yellow)]/25 text-[var(--pop-caramel)] font-bold">
                  <Shield size={12} className="inline mr-1" /> Superadmin
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input value={editForm.email} disabled className="opacity-70" />
                <p className="text-xs text-muted-foreground mt-1">Email kann nicht geändert werden.</p>
              </div>
              <div>
                <Label>Name *</Label>
                <Input required data-testid="input-edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div>
                <Label>Rolle</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={editIsSuperadmin}>
                  <SelectTrigger data-testid="select-edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="production">Produktion</SelectItem>
                    <SelectItem value="warehouse">Lager</SelectItem>
                    <SelectItem value="sales">Verkauf</SelectItem>
                  </SelectContent>
                </Select>
                {editIsSuperadmin && (
                  <p className="text-xs text-amber-700 mt-1">Superadmin-Rolle ist fest verdrahtet.</p>
                )}
              </div>
              <div>
                <Label className="flex items-center gap-1"><KeyRound size={12} /> Neues Passwort (optional)</Label>
                <Input type="password" minLength={6} placeholder="Leer lassen um nicht zu ändern" data-testid="input-edit-user-password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Berechtigungen</Label>
              <PermissionMatrix
                value={editIsSuperadmin ? undefined : editPerms}
                onChange={setEditPerms}
                disabled={!isSuperadmin || editIsSuperadmin}
              />
              {editIsSuperadmin && (
                <p className="text-xs text-amber-700 mt-2">
                  Der Superadmin hat immer alle Rechte – Änderungen an dieser Matrix sind gesperrt.
                </p>
              )}
              {!isSuperadmin && !editIsSuperadmin && (
                <p className="text-xs text-amber-700 mt-2">
                  Nur der Superadmin darf Rechte vergeben. Die Matrix bleibt unverändert.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setEditOpen(false)}>Abbrechen</Button>
              <Button type="submit" data-testid="btn-save-edit-user" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Speichern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
